// Third Party
import NextHead from 'next/head';

type Props = {
  canonicalUrl: string;
  noIndex: boolean;
  pageDescription: string;
  pageTitle: string;
  imageUrl?: string;
};

const openGraphImageUrl = 'https://www.savvy.com/opengraph-image.png';

export const Head: React.FC<Props> = ({
  noIndex,
  canonicalUrl,
  pageDescription,
  pageTitle,
  imageUrl,
}) => {
  return (
    <NextHead>
      <title key="title">{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* preconnect to required domains for LCP */}
      <link rel="preconnect" href="https://media.savvy.com"></link>
      <link rel="preconnect" href="https://www.google-analytics.com"></link>
      <link rel="preconnect" href="https://www.googletagmanager.com"></link>

      {/**
       * prefetch as fallback for preconnect support and
       * optimization for any required domains for LCP / Page load
       */}
      <link rel="dns-prefetch" href="https://www.google-analytics.com"></link>
      <link rel="dns-prefetch" href="https://www.googletagmanager.com"></link>
      <link rel="dns-prefetch" href="https://vitals.vercel-insights.com"></link>
      <link rel="dns-prefetch" href="https://media.savvy.com"></link>
      {/* autoSessionTracking is disabled for evaluation, no need for sentry prefetch */}
      {/* <link rel="dns-prefetch" href="https://o295134.ingest.sentry.io"></link> */}

      {/* https://developers.google.com/search/docs/advanced/crawling/block-indexing */}
      <meta
        name="robots"
        content={noIndex ? 'noindex, nofollow' : 'index, follow'}
      />

      <meta content="utf-8" httpEquiv="encoding" />
      <meta content="text/html;charset=utf-8" httpEquiv="Content-Type" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1"
      />

      <link
        rel="icon"
        type="image/png"
        href="/favicons/icon.png"
        sizes="96x96"
      />
      <link rel="icon" type="image/svg+xml" href="/favicons/icon.svg" />
      <link rel="shortcut icon" href="/favicons/icon.ico" />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/favicons/apple-icon.png"
      />
      <meta name="apple-mobile-web-app-title" content="Savvy" />
      <link rel="manifest" href="/favicons/site.webmanifest" />

      {/**
       * Twitter embed metadata
       * @see https://developer.twitter.com/en/docs/tweets/optimize-with-cards/overview/markup.html
       */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content="@Savvy" />
      <meta name="twitter:description" content={pageDescription} />
      <meta
        name="twitter:image"
        content={imageUrl ? imageUrl : openGraphImageUrl}
      />
      <meta name="twitter:site" content="@Savvy" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:url" content={canonicalUrl} />

      {/**
       * Facebook Open Graph meetadata
       * @see http://ogp.me/
       *
       * Many metadata fields are used for both SEO and Open Graph. Search engines use the "name" attribute,
       * and Open Graph uses the "property" attribute.
       * @see https://stackoverflow.com/questions/22350105/whats-the-difference-between-meta-name-and-meta-property
       */}
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" name="author" content="Savvy" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" name="title" content={pageTitle} />
      <meta
        property="og:image"
        name="image"
        content={imageUrl ? imageUrl : openGraphImageUrl}
      />
      <meta
        property="og:description"
        name="description"
        content={pageDescription}
      />
      <meta property="og:type" content="website" />
      <meta
        name="facebook-domain-verification"
        content="i323zc9ot2p2fdu66w7wa4og1l0j6k"
      />
    </NextHead>
  );
};
